import {
  AfterViewInit,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Input,
  IterableDiffers,
  OnDestroy,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import {DomHandlerService} from '@catmodules/widgets/growl/services/dom-handler.service';
import {MessageService} from '@catmodules/widgets/growl/services/message.service';
import {Message} from '@catmodules/widgets/growl/types';
import {Subscription} from 'rxjs/Subscription';

/**
 * Growl component based on primeng/growl
 */
@Component({
  selector: 'cat-growl',
  template: `
    <div #container [ngClass]="'ui-growl ui-widget'" [ngStyle]="style" [class]="styleClass">
      <div
        #msgel
        *ngFor="let msg of value;let i = index"
        class="ui-growl-item-container ui-state-highlight ui-corner-all ui-shadow"
           aria-live="polite"
           [ngClass]="{'ui-growl-message-info':msg.severity == 'info','ui-growl-message-warn':msg.severity == 'warn',
                    'ui-growl-message-error':msg.severity == 'error','ui-growl-message-success':msg.severity == 'success'}"
           (click)="onMessageClick(i)" (mouseenter)="onMessageHover(i)">
        <div class="ui-growl-item">
          <div class="ui-growl-icon-close fa fa-close" (click)="remove(i,msgel)"></div>
          <span class="ui-growl-image fa fa-2x"
                [ngClass]="{'fa-info-circle':msg.severity == 'info','fa-exclamation-circle':msg.severity == 'warn',
                                'fa-close':msg.severity == 'error','fa-check':msg.severity == 'success'}"></span>
          <div class="ui-growl-message">
            <span #headline class="ui-growl-title">{{msg.summary}}</span>
            <p #message [innerHTML]="msg.detail"></p>
          </div>
          <div style="clear: both;"></div>
        </div>
      </div>
    </div>
  `,
  providers: [DomHandlerService],
})
export class GrowlComponent implements DoCheck, OnDestroy {

  @Input() life = 3000;

  @Input() style: any;

  @Input() styleClass: string;

  @Input() immutable = true;

  @Input() autoZIndex = true;

  @Input() baseZIndex = 0;

  @Output() onClick: EventEmitter<any> = new EventEmitter();
  @Output() onClickOutside: EventEmitter<any> = new EventEmitter();

  @Output() onHover: EventEmitter<any> = new EventEmitter();

  @Output() onClose: EventEmitter<any> = new EventEmitter();

  @Output() valueChange: EventEmitter<Message[]> = new EventEmitter<Message[]>();

  @ViewChild('container') containerViewChild: ElementRef;

  _value: Message[];

  timeouts = [];

  preventRerender: boolean;

  differ: any;

  subscription: Subscription;

  closeIconClick: boolean;

  constructor(public el: ElementRef, public domHandler: DomHandlerService, public differs: IterableDiffers, @Optional() public messageService: MessageService) {
    this.differ = differs.find([]).create(null);

    if (messageService) {
      this.subscription = messageService.messageObserver.subscribe((messages) => {
        if (messages) {
          if (messages instanceof Array) {
            this.value = this.value ? [...this.value, ...messages] : [...messages];
          } else {
            this.value = this.value ? [...this.value, ...[messages]] : [messages];
          }
        }else {
          this.value = null;
        }
      });
    }
  }

  @Input() get value(): Message[] {
    return this._value;
  }

  set value(val: Message[]) {
    this._value = val;
    if (this.containerViewChild && this.containerViewChild.nativeElement && this.immutable) {
      this.handleValueChange();
    }
  }

  ngDoCheck() {
    if (!this.immutable && this.containerViewChild && this.containerViewChild.nativeElement) {
      const changes = this.differ.diff(this.value);
      if (changes) {
        this.handleValueChange();
      }
    }
  }

  handleValueChange() {
    if (this.preventRerender) {
      this.preventRerender = false;
      return;
    }

    this._value
      .filter((message) => !message.sticky)
      .forEach((message) => this.initTimeout(message));

    if (this.autoZIndex) {
      this.containerViewChild.nativeElement.style.zIndex = String(this.baseZIndex + (++DomHandlerService.zindex));
    }
    this.domHandler.fadeIn(this.containerViewChild.nativeElement, 250);
  }

  initTimeout(message) {
    this.timeouts.push(setTimeout(() => {
      const index = this._value.indexOf(message);
      const divElems = this.containerViewChild.nativeElement.querySelectorAll('div.ui-growl-item-container');
      if (index >= 0 && divElems.length > index) {
        this.remove(index, divElems[index]);
      }
    }, this.life));
  }

  remove(index: number, msgel: any) {
    this.closeIconClick = true;
    this.domHandler.fadeOut(msgel, 250);

    setTimeout(() => {
      this.preventRerender = true;
      this.onClose.emit({message: this.value[index]});

      if (this.immutable) {
        this._value = this.value.filter((val, i) => i != index);
        this.valueChange.emit(this._value);
      }
      else {
        this._value.splice(index, 1);
      }
    }, 250);
  }

  removeAll() {
    if (this.value && this.value.length) {
      this.domHandler.fadeOut(this.containerViewChild.nativeElement, 250);

      setTimeout(() => {
        this.value.forEach((msg, index) => this.onClose.emit({message: this.value[index]}));
        if (this.immutable) {
          this.value = [];
          this.valueChange.emit(this.value);
        }
        else {
          this.value.splice(0, this.value.length);
        }
      }, 250);
    }
  }

  onMessageClick(i: number) {
    if (this.closeIconClick)
      this.closeIconClick = false;
    else
      this.onClick.emit({message: this.value[i]});
  }

  onMessageHover(i: number) {
    this.onHover.emit({message: this.value[i]});
  }

  ngOnDestroy() {
    this.timeouts.forEach((timeout) => {
      clearTimeout(timeout);
    });
    this.timeouts = [];

    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_release@2/app/Resources/Public/src/app/modules/widgets/growl/components/growl.component.ts